import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Languages, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Navigation() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.language-menu')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLanguageChange = async (lng: string) => {
    try {
      await i18n.changeLanguage(lng);
      localStorage.setItem('i18nextLng', lng);
      setIsOpen(false);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const navLinks = (
    <>
      <Link to="/" className="hover:text-stone-900 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.home')}</Link>
      <Link to="/rooms" className="hover:text-stone-900 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.rooms')}</Link>
      <Link to="/restaurants" className="hover:text-stone-900 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.restaurants')}</Link>
      <Link to="/availability" className="hover:text-stone-900 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.availability')}</Link>
    </>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm shadow-sm">
      {/* Desktop Navigation */}
      <div className="hidden md:flex items-start px-6 py-1 w-full">
        <Link to="/" className="flex items-start mr-auto">
          <img src="/Sans_titre__Carte_de_visiyte_-removebg-preview.png" alt="Il Galileo Rooms" className="h-16" />
        </Link>
        <div className="flex items-center gap-6">
          <div className="flex gap-6 text-stone-700 text-sm">
            {navLinks}
          </div>

          <div className="relative language-menu">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
              className="flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white px-4 py-2.5 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <Languages className="w-5 h-5" />
              <span className="text-sm font-semibold">{currentLanguage.flag}</span>
            </button>

            {isOpen && (
              <div className="absolute right-0 mt-2 py-2 w-52 bg-white rounded-xl shadow-2xl border border-stone-200">
                {languages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageChange(language.code)}
                    className={`flex items-center justify-between w-full px-4 py-3 text-sm transition-all ${
                      i18n.language === language.code
                        ? 'bg-stone-900 text-white font-semibold'
                        : 'text-stone-700 hover:bg-stone-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{language.flag}</span>
                      <span>{language.name}</span>
                    </div>
                    {i18n.language === language.code && (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden">
        <div className="flex justify-between items-start px-4 py-1">
          <Link to="/" className="flex items-start">
            <img src="/Sans_titre__Carte_de_visiyte_-removebg-preview.png" alt="Il Galileo Rooms" className="h-14" />
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-stone-700 p-2"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white bg-opacity-95 border-t border-stone-200">
            <div className="px-4 py-2">
              <div className="flex flex-col gap-4 text-stone-700 mb-4">
                {navLinks}
              </div>

              <div className="border-t border-stone-200 pt-4">
                <p className="text-stone-700 font-semibold text-sm mb-3">{t('nav.selectLanguage')}</p>
                <div className="grid grid-cols-1 gap-2">
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => {
                        handleLanguageChange(language.code);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                        i18n.language === language.code
                          ? 'bg-stone-900 text-white font-semibold shadow-md'
                          : 'text-stone-700 hover:bg-stone-100 border border-stone-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{language.flag}</span>
                        <span className="text-sm">{language.name}</span>
                      </div>
                      {i18n.language === language.code && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}